// © 2026 KaraokeClubs. All rights reserved.

"use client";

import { motion } from "framer-motion";
import { t } from "@/lib/i18n";
import type { TonightTimeline as TimelineData } from "@/lib/venue-helpers";
import type { Locale } from "@/lib/types";

interface TonightTimelineProps {
  data: TimelineData;
  locale: Locale;
  /** Currently-selected hour (filters the list/map), or null. */
  activeHour: number | null;
  onSelectHour: (hour: number | null) => void;
  compact?: boolean;
}

/**
 * A glanceable hour-ribbon of "tonight" — bar height = how many venues are
 * live each hour, with a pulsing NOW marker. Tap an hour to spotlight the
 * venues live then. Answers "when does it start / when does it peak?" instantly.
 */
export default function TonightTimeline({ data, locale, activeHour, onSelectHour, compact }: TonightTimelineProps) {
  const { hours, peak } = data;
  if (!hours.length || peak === 0) return null;

  const barH = compact ? 30 : 38;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className="relative overflow-hidden rounded-2xl border border-white/[0.07] bg-gradient-to-br from-zinc-900/80 to-zinc-950/60 px-3.5 pt-2.5 pb-2 backdrop-blur-sm"
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] font-bold tracking-wide text-zinc-300 uppercase">
          {t(locale, "timeline_title")}
        </span>
        {activeHour !== null && (
          <button
            onClick={() => onSelectHour(null)}
            className="text-[10px] font-semibold text-violet-300/90 hover:text-violet-200 px-1.5 py-0.5 rounded-md bg-violet-500/10 active:scale-95 transition touch-manipulation"
          >
            {t(locale, "timeline_clear")}
          </button>
        )}
      </div>

      <div className="flex items-end gap-[3px]" style={{ height: barH }}>
        {hours.map((h, i) => {
          const ratio = peak > 0 ? h.count / peak : 0;
          const hPct = Math.max(h.count > 0 ? 0.18 : 0.05, ratio); // floor so non-zero is visible
          const active = activeHour === h.hour;
          const dim = activeHour !== null && !active;

          const base = h.isNow
            ? "from-emerald-400 to-emerald-600"
            : h.count > 0
              ? "from-violet-400 to-fuchsia-600"
              : "from-zinc-700 to-zinc-800";

          return (
            <button
              key={`${h.hour}-${i}`}
              onClick={() => onSelectHour(active ? null : h.hour)}
              title={`${h.label}:00 · ${h.count}`}
              className="group relative flex-1 flex items-end h-full touch-manipulation"
              aria-label={`${h.label}:00, ${h.count} venues`}
            >
              <span
                className={`w-full rounded-[3px] bg-gradient-to-t ${base} transition-all duration-200
                  ${active ? "ring-2 ring-white/70 ring-offset-1 ring-offset-zinc-950" : ""}
                  ${dim ? "opacity-30" : "opacity-95 group-hover:opacity-100"}`}
                style={{ height: `${hPct * 100}%` }}
              />
              {h.isNow && (
                <span className="pointer-events-none absolute -top-0.5 left-1/2 -translate-x-1/2 flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-300" />
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Hour axis — show NOW + a few anchor labels so it stays uncluttered. */}
      <div className="flex items-center gap-[3px] mt-1">
        {hours.map((h, i) => {
          const show = h.isNow || h.hour % 3 === 0;
          const active = activeHour === h.hour;
          return (
            <span
              key={`l-${h.hour}-${i}`}
              className={`flex-1 text-center text-[8.5px] font-semibold tabular-nums leading-none
                ${h.isNow ? "text-emerald-300" : active ? "text-violet-200" : "text-zinc-600"}`}
            >
              {show ? (h.isNow ? t(locale, "timeline_now") : h.label) : ""}
            </span>
          );
        })}
      </div>
    </motion.div>
  );
}
